import React, { useState } from 'react';
import { Phone, ShieldCheck, ShieldAlert, AlertTriangle, ArrowRight, CheckCircle2, FileText, Gavel, UserCheck, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VoiceLayout } from './VoiceLayout';
import { AI_CALLS, VOICE_AGENTS } from '@/data/voiceAgents';
import { usePageTitle } from '@/lib/usePageTitle';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const OUTCOME_TONE = {
  'Paid':              'success', 'Plan Created': 'primary', 'Promise to Pay': 'success',
  'Hardship Captured': 'info',    'Dispute Captured': 'warning',
  'Escalated':         'warning', 'Refused · DNC set': 'destructive', 'Wrong Party': 'muted',
};
const COMPLIANCE_TONE = { 'Passed': 'success', 'Warning': 'warning', 'Failed': 'destructive' };

export default function CallReview() {
  usePageTitle('Call Review & QA');
  const [filter, setFilter] = useState({ outcome: 'all', compliance: 'all', sentiment: 'all', q: '' });
  const [open, setOpen] = useState(null);

  const list = AI_CALLS.filter((c) => {
    if (filter.outcome !== 'all' && c.outcome !== filter.outcome) return false;
    if (filter.compliance !== 'all' && c.complianceStatus !== filter.compliance) return false;
    if (filter.sentiment !== 'all' && c.sentiment !== filter.sentiment) return false;
    if (filter.q && !(c.debtorName + c.aiAgent + c.callId).toLowerCase().includes(filter.q.toLowerCase())) return false;
    return true;
  });

  const outcomes = [...new Set(AI_CALLS.map((c) => c.outcome))];

  return (
    <VoiceLayout
      title="Call review & QA"
      subtitle="Supervisor and legal review queue for every AI-handled conversation, with transcript, AI-generated summary, compliance checklist and one-click action paths."
    >
      <div className="flex flex-wrap gap-2 items-center">
        <Input className="h-9 w-56" placeholder="Search debtor / call ID / agent" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} aria-label="Filter call reviews" />
        <FilterSelect label="Outcome"   value={filter.outcome}    onChange={(v) => setFilter({ ...filter, outcome: v })}    options={['all', ...outcomes]} />
        <FilterSelect label="Compliance" value={filter.compliance} onChange={(v) => setFilter({ ...filter, compliance: v })} options={['all', 'Passed', 'Warning', 'Failed']} />
        <FilterSelect label="Sentiment"  value={filter.sentiment}  onChange={(v) => setFilter({ ...filter, sentiment: v })}  options={['all', 'Positive', 'Neutral', 'Stressed', 'Angry']} />
        <span className="ml-auto text-[11px] text-muted-foreground">Showing {list.length} of {AI_CALLS.length}</span>
      </div>

      <div className="surface-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-widest">
            <tr>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">Call ID</th>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">Debtor</th>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">Campaign</th>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">AI Agent</th>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">Outcome</th>
              <th scope="col" className="text-right px-3 py-2.5 font-medium">PTP</th>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">Compliance</th>
              <th scope="col" className="text-left px-3 py-2.5 font-medium">Sentiment</th>
              <th scope="col" className="text-right px-3 py-2.5 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {list.slice(0, 20).map((c) => (
              <tr key={c.callId} className="border-t border-border hover:bg-muted/40 cursor-pointer" onClick={() => setOpen(c)} data-testid={`review-row-${c.callId}`}>
                <td className="px-3 py-2 font-mono-num text-[12px]">{c.callId}</td>
                <td className="px-3 py-2"><div><p>{c.debtorName}</p><p className="text-[10px] text-muted-foreground font-mono-num">{c.phoneMasked}</p></div></td>
                <td className="px-3 py-2 text-[12px] text-muted-foreground truncate max-w-[200px]">{c.campaign}</td>
                <td className="px-3 py-2">{c.aiAgent}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={cn('text-[10px]',
                    OUTCOME_TONE[c.outcome] === 'success' && 'border-success/40 bg-success/10 text-success',
                    OUTCOME_TONE[c.outcome] === 'warning' && 'border-warning/40 bg-warning/10 text-warning',
                    OUTCOME_TONE[c.outcome] === 'destructive' && 'border-destructive/40 bg-destructive/10 text-destructive',
                    OUTCOME_TONE[c.outcome] === 'info' && 'border-info/40 bg-info/10 text-info',
                    OUTCOME_TONE[c.outcome] === 'primary' && 'border-primary/40 bg-primary/10 text-gold-deep',
                  )}>{c.outcome}</Badge>
                </td>
                <td className="px-3 py-2 text-right font-mono-num text-[12px]">{c.promiseToPayAmount ? c.promiseToPayAmount.toLocaleString() : '—'}</td>
                <td className="px-3 py-2">
                  <span className={cn('inline-flex items-center gap-1 text-[11px]',
                    COMPLIANCE_TONE[c.complianceStatus] === 'success' && 'text-success',
                    COMPLIANCE_TONE[c.complianceStatus] === 'warning' && 'text-warning',
                    COMPLIANCE_TONE[c.complianceStatus] === 'destructive' && 'text-destructive',
                  )}>
                    {c.complianceStatus === 'Passed' ? <ShieldCheck className="h-3 w-3" aria-hidden="true" /> : <ShieldAlert className="h-3 w-3" aria-hidden="true" />}
                    {c.complianceStatus}
                  </span>
                </td>
                <td className="px-3 py-2 text-[11px]">{c.sentiment}</td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={(e) => { e.stopPropagation(); setOpen(c); }}>Review <ArrowRight className="h-3 w-3 ml-1" aria-hidden="true" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CallDetail call={open} onClose={() => setOpen(null)} />
    </VoiceLayout>
  );
}

const FilterSelect = ({ label, value, onChange, options }) => (
  <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
    {label}
    <select value={value} onChange={(e) => onChange(e.target.value)} className="h-8 rounded-md border border-border bg-card text-[12px] px-2">
      {options.map((o) => <option key={o} value={o}>{o === 'all' ? 'All' : o}</option>)}
    </select>
  </label>
);

const CHECKLIST = [
  'Identity verified before debt disclosure',
  'Recording disclosure played',
  'No threats / harassment detected',
  'Sensitive PII masked in transcript',
  'Contact-window respected',
  'Frequency cap respected',
  'Approved script library used',
];

const CallDetail = ({ call, onClose }) => {
  if (!call) return null;
  return (
    <Dialog open={!!call} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="call-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{call.callId}</span>
            <Badge variant="outline" className="text-[10px]">{call.outcome}</Badge>
            {call.complianceStatus !== 'Passed' && <Badge variant="outline" className="text-[10px] border-warning/40 bg-warning/10 text-warning">{call.complianceStatus}</Badge>}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Call review detail with transcript, AI-generated summary, compliance checklist and supervisor actions for {call.callId}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <p className="text-[10px] uppercase tracking-widest text-primary">Transcript</p>
            <div className="h-72 overflow-y-auto scrollbar-thin space-y-1.5 rounded-md border border-border p-3 bg-muted/30" dir="rtl">
              {call.transcript?.map((l, i) => (
                <div key={i} className={cn('flex', l.who === 'agent' ? 'justify-start' : 'justify-end')}>
                  <div className={cn('max-w-[80%] rounded-2xl px-3 py-1.5 text-[12px]', l.who === 'agent' ? 'bg-primary/10' : 'bg-onyx text-background')}>
                    <span className="text-[9px] uppercase tracking-widest opacity-60 block mb-0.5" dir="ltr">{l.who === 'agent' ? call.aiAgent : call.debtorName}</span>
                    {l.ar}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-primary">AI-generated summary</p>
              <p className="text-[13px] leading-relaxed mt-1">{call.summary}</p>
            </div>

            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <Datum label="Identity verification" value={call.verificationStatus} />
              <Datum label="Sentiment" value={call.sentiment} />
              <Datum label="Promise to Pay" value={call.promiseToPayAmount ? `SAR ${call.promiseToPayAmount.toLocaleString()}` : '—'} />
              <Datum label="Payment date" value={call.promiseToPayDate || '—'} />
              <Datum label="Dispute flag" value={call.disputeFlag ? 'Yes' : 'No'} />
              <Datum label="Hardship flag" value={call.hardshipFlag ? 'Yes' : 'No'} />
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-widest text-primary mb-1">Compliance checklist</p>
              <ul className="space-y-1">
                {CHECKLIST.map((c, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11.5px]">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-success shrink-0" aria-hidden="true" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5 text-[11.5px]">
              <span className="font-medium text-primary">Recommended next:</span> {call.nextAction}
            </div>

            <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border">
              <Button size="sm" className="h-7 text-[11px] bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => { toast.success('Summary approved'); onClose(); }}><CheckCircle2 className="h-3 w-3 mr-1" aria-hidden="true" />Approve summary</Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => toast.message('Outcome correction logged')}><FileText className="h-3 w-3 mr-1" aria-hidden="true" />Correct outcome</Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => toast.message('Escalated to Legal')}><Gavel className="h-3 w-3 mr-1" aria-hidden="true" />Escalate to Legal</Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => toast.message('Assigned to human agent')}><UserCheck className="h-3 w-3 mr-1" aria-hidden="true" />Assign to human</Button>
              <Button size="sm" variant="ghost"  className="h-7 text-[11px]" onClick={() => toast.message('Flagged for compliance review')}><Flag className="h-3 w-3 mr-1" aria-hidden="true" />Flag</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Datum = ({ label, value }) => (
  <div className="rounded-md bg-muted/40 p-2">
    <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="text-[12px] font-medium mt-0.5">{value}</p>
  </div>
);
